import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { defer, EMPTY, expand, finalize, from, Observable, takeWhile, throwError } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

/**
 * Reads a fetch `Response` body as a Server-Sent Events (SSE) stream.
 *
 * Owns the {@link AbortController} used to cancel the underlying `fetch` request,
 * decodes the incoming bytes to text, and buffers partial chunks so they can be
 * split into complete, newline-delimited SSE event frames as they arrive.
 */
class SseStreamReader {
  private readonly abortController = new AbortController();
  private readonly textDecoder = new TextDecoderStream();
  private bodyReader: ReadableStreamDefaultReader<string>;
  private eventBuffer = '';
  private isDone = false;

  /**
   * Attaches the reader to a fetch `Response` and returns an observable of its first chunk.
   * @param {Response} response The response whose body should be streamed.
   * @return {Observable<ReadableStreamReadResult<string>>} An observable that emits the next read result on subscription.
   */
  public open = (response: Response): Observable<ReadableStreamReadResult<string>> => {
    this.bodyReader = response.body.pipeThrough(this.textDecoder).getReader();
    return this.next;
  };

  /**
   * Marks the stream as finished, e.g. once a `[DONE]` sentinel event has been received.
   */
  public finish() {
    this.isDone = true;
  }

  /**
   * The signal to pass to `fetch` so the request can be cancelled via {@link close}.
   * @return {AbortSignal} The abort signal tied to this reader's controller.
   */
  public get signal(): AbortSignal {
    return this.abortController.signal;
  }

  /**
   * Cancels the underlying request and releases the reader lock.
   * Safe to call multiple times, e.g. on completion, error, or unsubscribe.
   */
  public close() {
    this.abortController.abort();
    this.bodyReader?.releaseLock();
  }

  /**
   * Appends a decoded chunk to the buffer and extracts every complete SSE event frame it now contains.
   * Any trailing, not-yet-complete frame is kept in the buffer for the next chunk.
   * @param {string} chunk The decoded text chunk read from the response body.
   * @return {string[]} The raw SSE event frames completed by this chunk, each still containing its `data:` lines.
   */
  public splitIntoEvents(chunk: string): string[] {
    this.eventBuffer += chunk;
    const events = this.eventBuffer.split(/\r?\n\r?\n/);
    this.eventBuffer = events.pop() || '';
    return events;
  }

  /**
   * @return {Observable<ReadableStreamReadResult<string>>} An observable of the next read result each time it is subscribed to.
   */
  public get next(): Observable<ReadableStreamReadResult<string>> {
    return defer(() => from(this.bodyReader.read()));
  }

  /**
   * @return {boolean} Whether the stream has been marked as finished via {@link finish}.
   */
  public get done(): boolean {
    return this.isDone;
  }
}

@Injectable()
export class GaiaUserGuideService {

  /**
   * Sends a question to Gaia's user guide API and streams the response tokens as an observable.
   * Establishes a POST request to a streaming endpoint, processes the response,
   * and returns individual tokens extracted from the stream.
   * @param {string} question The question to be sent to Gaia's user guide API for obtaining guidance.
   * @return {Observable<string>} An observable that emits response tokens individually as strings until the stream ends.
   */
  public askGaiaUserGuide(question: string): Observable<string> {
    return defer(() => {
      const stream = new SseStreamReader();
      return from(fetch(`${environment.userGuide.baseUrl}/api-guide-mya/rag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': environment.userGuide.apiKey,
          Connection: 'keep-alive',
          Accept: 'text/event-stream'
        },
        body: JSON.stringify({question}),
        signal: stream.signal
      }))
        .pipe(
          switchMap(response => {
            if (!response.ok) {
              return throwError(() => new Error(`HTTP error ${response.status}`));
            }

            if (!response.body) {
              return throwError(() => new Error('Streaming not supported'));
            }

            return stream.open(response).pipe(
              // Continue reading chunks until the browser marks the stream as done or a [DONE] event is received.
              expand(result => result.done || stream.done ? EMPTY : stream.next),
            );
          }),
          filter(({value, done}) => !done && !!value),
          // Server-sent events are separated by a blank line.
          mergeMap(({value}) => from(stream.splitIntoEvents(value))),
          // Extract and combine all `data:` lines from the server-sent event.
          map(event => event
            .split(/\r?\n/)
            .filter(line => line.startsWith('data:'))
            .map(line => line.replace(/^data:\s?/, ''))
            .join('\n')
            .trim()
          ),
          // The API sends [DONE] to indicate that no more tokens will arrive.
          tap(data => {
            if (data === '[DONE]') stream.finish();
          }),
          filter(data => !!data && data !== '[DONE]')
        )
        .pipe(
          // Parse the event payload and emit only the streamed token value
          map(data => JSON.parse(data)?.token),
          filter(token => token !== undefined),
          // Complete the observable once the stream has been marked as finished.
          takeWhile(() => !stream.done, true),
          finalize(() => {
            // Ensure the request is cancelled and the stream reader is released on completion, error, or unsubscribe.
            stream.close();
          }),
        );
    });
  }
}
